import { Suspense, useMemo } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@rtkwlf/pro-solid-svg-icons/faInfoCircle';
import {
  QueryErrorResetBoundary,
  queryOptions,
  useQuery,
  useSuspenseQueries,
} from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import { Outlet } from 'react-router-dom';

import {
  useGetOrganizationOptions,
  useGetOrganizationSpidrBlueprintsOptions,
} from '@apis/track-public/query-options';
import { Chip } from '@packages/fenrir/components/chip';
import { IconButton } from '@packages/fenrir/components/icon-button';
import { Tooltip } from '@packages/fenrir/components/tooltip';
import { PageRootContent } from '@packages/fenrir/page-blocks/page/page-root';

import { ConciergeTierBadge } from '../../components/concierge-tier-badge/concierge-tier-badge';
import {
  Page,
  PageHeader,
  PageTitle,
  PageResizablePanel,
  PagePanelTitle,
  PagePreviewPanel,
  PageNavTabs,
  PageNavTab,
  PagePanelTabsContent,
  PageResizableContent,
  PagePanelHeader,
} from '../../components/page/page';
import { routes } from '../../constants/routes';
import { useSecurityJourneyTranslations } from '../../locales/use-security-journey-translations';

import { BlueprintDetails } from './_components/blueprint-details/blueprint-details';
import { getExcludedBlueprints } from './_utils/get-excluded-blueprints';
import { getFavouriteBlueprints } from './_utils/get-favourite-blueprints';
import { getIncludedBlueprints } from './_utils/get-included-blueprints';
import { SpidrLibraryPageErrorBoundary } from './error';
import styles from './layout.module.css';
import { SpidrLibraryPageLoading } from './loading';

/* -------------------------------------------------------------------------------------------------
 * TabCount
 * -----------------------------------------------------------------------------------------------*/
type TabCountProps = {
  count: number;
};
const TabCount = (props: TabCountProps) => {
  return <Chip label={props.count} className={styles['nav-tab-badge']} />;
};

/* -------------------------------------------------------------------------------------------------
 * SpidrLibraryContent
 * -----------------------------------------------------------------------------------------------*/
const SpidrLibraryContent = () => {
  /**
   * init translations
   */
  const { t } = useSecurityJourneyTranslations();

  /**
   * fetch all of the blueprints
   */
  const [{ data: blueprints }, { data: orgTier }] = useSuspenseQueries({
    queries: [
      queryOptions({
        ...useGetOrganizationSpidrBlueprintsOptions(),
        select: (data) => Object.values(data.blueprints),
      }),
      queryOptions({
        ...useGetOrganizationOptions(),
        select: (data) => data.tierInfo,
      }),
    ],
  });

  /**
   * Calculates the number of included blueprints
   */
  const included = useMemo(
    () => getIncludedBlueprints(blueprints, orgTier?.tier).length,
    [blueprints, orgTier],
  );

  /**
   *  Calculates the number of excluded blueprints
   */
  const excluded = useMemo(
    () => getExcludedBlueprints(blueprints, orgTier?.tier).length,
    [blueprints, orgTier],
  );

  /**
   *  Calculates the number of favourite blueprints
   */
  const favourited = useMemo(
    () => getFavouriteBlueprints(blueprints).length,
    [blueprints],
  );

  /**
   * (aaho): This is unfortunate, but the `tabs` components from MUI are
   * not smart enough to handle conditionally rendered tabs. The selected
   * tab completely falls over.
   *
   * see: https://github.com/mui/material-ui/issues/34740
   */
  const tabs = useMemo(() => {
    if (orgTier == null || orgTier.legacy) {
      return (
        <PageNavTabs>
          <PageNavTab
            label={t('spidrLibrary.all.tabTitle')}
            value={routes.spidrLibrary.all.index()}
            icon={<TabCount count={blueprints.length} />}
            iconPosition="end"
          />
          <PageNavTab
            label={t('spidrLibrary.favorites.tabTitle')}
            value={routes.spidrLibrary.favorites.index()}
            icon={<TabCount count={favourited} />}
            iconPosition="end"
          />
        </PageNavTabs>
      );
    }

    return (
      <PageNavTabs>
        <PageNavTab
          label={t('spidrLibrary.all.tabTitle')}
          value={routes.spidrLibrary.all.index()}
          icon={<TabCount count={blueprints.length} />}
          iconPosition="end"
        />
        <PageNavTab
          label={t('spidrLibrary.included.tabTitle')}
          value={routes.spidrLibrary.included.index()}
          icon={<TabCount count={included} />}
          iconPosition="end"
        />
        <PageNavTab
          label={t('spidrLibrary.excluded.tabTitle')}
          value={routes.spidrLibrary.excluded.index()}
          icon={<TabCount count={excluded} />}
          iconPosition="end"
        />
        <PageNavTab
          label={t('spidrLibrary.favorites.tabTitle')}
          value={routes.spidrLibrary.favorites.index()}
          icon={<TabCount count={favourited} />}
          iconPosition="end"
        />
      </PageNavTabs>
    );
  }, [favourited, included, excluded, blueprints, orgTier, t]);

  return (
    <>
      {tabs}
      <PagePanelTabsContent>
        <Outlet />
      </PagePanelTabsContent>
    </>
  );
};

export const SpidrLibraryLayout = () => {
  /**
   * init translations
   */
  const { t } = useSecurityJourneyTranslations();

  /**
   * fetch the current organizations tier
   */
  const { data: tier } = useQuery(
    queryOptions({
      ...useGetOrganizationOptions(),
      select: (data) => data.tierInfo,
    }),
  );

  return (
    <Page>
      <PageHeader>
        <div className={styles['page-title']}>
          <PageTitle>{t('spidrLibrary.title')}</PageTitle>
          {tier && !tier.legacy && (
            <ConciergeTierBadge.Organization tier={tier.tier} />
          )}
        </div>
      </PageHeader>
      <PageResizableContent>
        <PageResizablePanel>
          <PagePanelHeader>
            <div className={styles['panel-title']}>
              <PagePanelTitle>{t('spidrLibrary.subTitle')}</PagePanelTitle>
              <Tooltip
                title={t('spidrLibrary.description')}
                placement="right"
                arrow
              >
                <IconButton size="small">
                  <FontAwesomeIcon icon={faInfoCircle} size="sm" />
                </IconButton>
              </Tooltip>
            </div>
          </PagePanelHeader>
          <QueryErrorResetBoundary>
            {({ reset }) => {
              return (
                <ErrorBoundary
                  onReset={reset}
                  FallbackComponent={SpidrLibraryPageErrorBoundary}
                >
                  <Suspense fallback={<SpidrLibraryPageLoading />}>
                    <SpidrLibraryContent />
                  </Suspense>
                </ErrorBoundary>
              );
            }}
          </QueryErrorResetBoundary>
        </PageResizablePanel>
        <PagePreviewPanel>
          <BlueprintDetails />
        </PagePreviewPanel>
      </PageResizableContent>
    </Page>
  );
};

export const Component = () => (
  <PageRootContent>
    <SpidrLibraryLayout />
  </PageRootContent>
);
